import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

const inputClass =
  "block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition";

const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

const PRIORITY_OPTIONS  = ['Primary', 'Secondary', 'Tertiary'];
const APPROVED_OPTIONS  = ['Yes', 'No'];
const FORMAT_DEFAULTS   = ['URL', 'PDF', 'Book', 'Article', 'Other'];

export default function SuperadminSourcesNew({ source = {}, facets = {} }) {
  const [title, setTitle] = useState(source.title || '');
  const [authorOrganization, setAuthorOrganization] = useState(source.author_organization || '');
  const [url, setUrl] = useState(source.url_or_document_reference || '');
  const [format, setFormat] = useState(source.format || '');
  const [priorityLevel, setPriorityLevel] = useState(source.priority_level || '');
  const [sourceType, setSourceType] = useState(source.source_type || '');
  const [approvedForQuoting, setApprovedForQuoting] = useState(source.approved_for_quoting || '');
  const [submitting, setSubmitting] = useState(false);

  const formatOptions = Array.from(new Set([...(facets.formats || []), ...FORMAT_DEFAULTS]));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    router.post('/superadmin/sources', {
      source: {
        title: title.trim(),
        author_organization: authorOrganization,
        url_or_document_reference: url,
        format,
        priority_level: priorityLevel,
        source_type: sourceType,
        approved_for_quoting: approvedForQuoting,
      },
    }, {
      onFinish: () => setSubmitting(false),
    });
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Superadmin
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Add source</h1>
          <p className="mt-1 text-sm text-gray-500">Create a research source.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-5">
          <div>
            <label htmlFor="title" className={labelClass}>Title <span className="text-red-500">*</span></label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
              placeholder="e.g. CDC Hand Hygiene in Healthcare Settings"
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="author_organization" className={labelClass}>Author / Organization</label>
            <input
              type="text"
              id="author_organization"
              value={authorOrganization}
              onChange={(e) => setAuthorOrganization(e.target.value)}
              placeholder="e.g. Centers for Disease Control and Prevention"
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="url_or_document_reference" className={labelClass}>URL or document reference</label>
            <input
              type="url"
              id="url_or_document_reference"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="source_type" className={labelClass}>Source type</label>
              <input
                type="text"
                id="source_type"
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                list="source-type-options"
                placeholder="e.g. Report"
                className={inputClass}
              />
              <datalist id="source-type-options">
                {(facets.source_types || []).map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
            <div>
              <label htmlFor="format" className={labelClass}>Format</label>
              <input
                type="text"
                id="format"
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                list="format-options"
                placeholder="e.g. URL"
                className={inputClass}
              />
              <datalist id="format-options">
                {formatOptions.map((f) => <option key={f} value={f} />)}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="priority_level" className={labelClass}>Priority level</label>
              <select
                id="priority_level"
                value={priorityLevel}
                onChange={(e) => setPriorityLevel(e.target.value)}
                className={inputClass}
              >
                <option value="">—</option>
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="approved_for_quoting" className={labelClass}>Approved for quoting</label>
              <select
                id="approved_for_quoting"
                value={approvedForQuoting}
                onChange={(e) => setApprovedForQuoting(e.target.value)}
                className={inputClass}
              >
                <option value="">—</option>
                {APPROVED_OPTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <Link
              href="/superadmin/sources"
              className="flex-1 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
            >
              {submitting ? 'Saving…' : 'Create source'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
